import Link from 'next/link'
import { NextSeo } from 'next-seo'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { Button } from '@components/UI/Button'
import {
  ArchiveIcon,
  ArrowRightIcon,
  CloudDownloadIcon,
  DocumentIcon,
  ShieldCheckIcon
} from '@heroicons/react/solid'

export default function Documents() {
  return (
    <>
      <NextSeo
        title="Upload Your Property's Documents | Home Repair Pricing"
        description="Centralize your property's documents in one-easy-to access location. Upload items such as colors, receipts, repair pricing guides & more. Get started now."
        canonical="https://majordomo.com/upload-property-documents"
        openGraph={{
          url: 'https://majordomo.com/upload-property-documents',
          title:
            "Upload Your Property's Documents | Home Repair Pricing | Majordomo",
          description:
            "Centralize your property's documents in one-easy-to access location. Upload items such as colors, receipts, repair pricing guides & more. Get started now.",
          images: [
            {
              url:
                'https://majordomo.com/images/product-pages/documents-hero.png',
              width: 700,
              height: 680,
              alt:
                'A keyboard with a blue archive button and binders sitting on top of the button'
            }
          ]
        }}
      />
      <Header />

      <main className="pt-8 px-0 container mx-auto">
        <div className="flex md:flex-row flex-col justify-between items-center lg:pb-36 pb-12">
          <div className="flex flex-col md:items-start items-center justify-center w-full 2xl:p-36 xl:p-20 md:p-8 md:order-first order-last">
            <div className="flex items-center border-b border-gray-200 mb-4 pb-4 md:w-full">
              <DocumentIcon
                width={36}
                height={36}
                className="pr-2 mr-2 border-r border-gray-200 text-brand"
              />
              <h1 className="text-2xl font-black tracking-wide text-brand uppercase">
                Documents
              </h1>
            </div>

            <p className="lg:mb-8 md:mb-4 mb-8 lg:text-2xl md:text-lg text-xl font-light max-w-md">
              <span className="font-black">Centralize</span> your property's
              documents in one easy-to-access location
            </p>

            <div className="w-full max-w-lg md:p-0 px-8 space-y-4">
              <p>
                Save your home inspection report, photos and other important
                documents about your property to Majordomo Documents and access
                them from any web browser.
              </p>

              <p>
                Save items such as pictures of paint can colors, important
                receipts, roofing type or warranty certificates. No more
                rummaging through boxes to find these documents.
              </p>

              <p>
                <span className="font-black">Free forever</span> with every
                Domoreport.
              </p>
            </div>
          </div>

          <img
            src="/images/product-pages/documents-hero.png"
            className="md:order-last order-first md:mb-0 mb-8"
            width="50%"
          />
        </div>

        <div className="lg:mb-36 mb-12">
          <div className="flex lg:flex-row flex-col justify-evenly 2xl:px-16 px-4">
            <div className="flex items-center justify-center lg:w-1/2 w-full lg:p-16">
              <img
                src="/images/product-pages/documents.png"
                alt="Majordomo product samples"
                className="xl:max-w-lg lg:max-w-sm max-w-md lg:w-auto"
              />
            </div>

            <div className="space-y-12 md:p-16 p-4 lg:w-1/2 w-full flex flex-col items-center">
              <div className="flex relative">
                <ShieldCheckIcon
                  width={56}
                  height={56}
                  className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 text-white bg-accentBlue p-2"
                />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Peace of mind
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Keep your important documents safe in the cloud for the
                    lifetime of your account. You – and nobody else – decide who
                    to share your documents with. You can even share your
                    documents with future buyers.
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <CloudDownloadIcon
                  width={56}
                  height={56}
                  className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 text-white bg-accentBlue p-2"
                />
                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Access files anywhere
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Sign in to majordomo.com to access everything you’ve stored
                    on Majordomo from any browser—no software installation
                    required.
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <ArchiveIcon
                  width={56}
                  height={56}
                  className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 text-white bg-accentBlue p-2"
                />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Unlimited storage
                  </h4>
                  <p className="max-w-lg mr-auto">
                    When you order a Domoreport you can store all your documents
                    and photos without having to worry about how much storage
                    space you have left.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <h4 className="text-center md:text-2xl text-lg font-black tracking-tight md:px-0 px-8 pb-12">
          See how easy it is to upload documents
        </h4>

        <div className="flex items-center justify-center w-full lg:pb-36 pb-12 lg:p-0 px-8">
          <iframe
            width="840"
            height="472.4"
            src="https://www.youtube.com/embed/bRnA9xc21NA?hl=en_US&amp;rel=0&amp;autohide=1&amp;autoplay=0"
            frameBorder="0"
            allow="accelerometer; autoplay; encrypted-media"
            title="Introducing Majordomo's Domoreport"
            className="lg:inline hidden rounded shadow-sm"
            allowFullScreen
          />

          <iframe
            width="560"
            height="315"
            src="https://www.youtube.com/embed/bRnA9xc21NA?hl=en_US&amp;rel=0&amp;autohide=1&amp;autoplay=0"
            frameBorder="0"
            allow="accelerometer; autoplay; encrypted-media"
            title="Introducing Majordomo's Domoreport"
            className="lg:hidden inline rounded shadow-sm"
            allowFullScreen
          />
        </div>

        <div className="lg:mb-36 mb-12">
          <h4 className="text-center md:text-2xl text-lg font-black tracking-tight md:px-0 px-8 pb-12">
            Get started with a Domoreport
          </h4>

          <div className="flex md:flex-row flex-col justify-evenly items-center w-full md:space-y-0 space-y-4 md:pb-12 pb-0">
            <Link href="/checkout">
              <a>
                <Button variant="primary" clipSide="right">
                  Get repair estimates{' '}
                  <ArrowRightIcon width={18} height={18} className="ml-2" />
                </Button>
              </a>
            </Link>

            <Link href="/features/domoreport-repair-estimates">
              <a className="flex items-center justify-center font-bold text-accentBlue hover:underline">
                Learn More{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </a>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </>
  )
}
